import { Box, Chip, MenuItem, TextField, Tooltip } from '@mui/material';
import { useFeature } from '../admin/featureFlags/FeatureGate';

/**
 * „Źródło ustalenia” przy ręcznej poprawce prezlecenia (paczka p. Mariusza
 * 21.10, BR-CS-14 w części bez poczty; przełącznik `preOrderAgreementSource`,
 * S-21.10-15; API `pre-order/agreement-source.ts`): skąd CS wie o zmianie,
 * gdy system tego nie widzi. Rodzaje to dane z API.
 */
export const FEATURE_PRE_ORDER_AGREEMENT_SOURCE = 'preOrderAgreementSource';

export const AGREEMENT_SOURCE_CHOICES: ReadonlyArray<{ id: string; name: string }> = [
  { id: 'phone', name: 'telefon' },
  { id: 'email', name: 'mail poza systemem' },
  { id: 'verbal', name: 'ustalenie ustne' },
  { id: 'other', name: 'inne' },
];

/** Najkrótszy opis przy „inne” — jak w API. */
export const MIN_OTHER_NOTE = 5;

export interface AgreementSourceInput {
  kind: string;
  note?: string;
}

/** Czy źródło da się zapisać: brak — tak; „inne” — z opisem. */
export function agreementSourceReady(s: AgreementSourceInput | null | undefined): boolean {
  if (!s || !s.kind) return true;
  return s.kind !== 'other' || (s.note ?? '').trim().length >= MIN_OTHER_NOTE;
}

/** Pola formularza: rodzaj i opis; puste = bez źródła (zapis jak dotąd). */
export const AgreementSourceFields = ({
  value,
  onChange,
  disabled = false,
}: {
  value: AgreementSourceInput | null | undefined;
  onChange: (v: AgreementSourceInput | null) => void;
  disabled?: boolean;
}) => {
  const on = useFeature(FEATURE_PRE_ORDER_AGREEMENT_SOURCE);
  if (!on) return null;
  const kind = value?.kind ?? '';
  const note = value?.note ?? '';
  const needsNote = kind === 'other' && note.trim().length < MIN_OTHER_NOTE;
  return (
    <Box
      sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))' }}
      data-testid="pre-order-agreement-source"
    >
      <TextField
        select
        size="small"
        label="Źródło ustalenia"
        value={kind}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value ? { kind: e.target.value, note } : null)}
        helperText="Skąd wiesz o zmianie — trafi do historii każdego zmienionego pola"
        slotProps={{ htmlInput: { 'data-testid': 'pre-order-agreement-source-kind' } }}
      >
        <MenuItem value="">— bez źródła —</MenuItem>
        {AGREEMENT_SOURCE_CHOICES.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label="Opis źródła"
        value={note}
        disabled={disabled || kind === ''}
        error={needsNote}
        onChange={(e) => onChange({ kind, note: e.target.value })}
        helperText={kind === 'other' ? `Przy „inne” obowiązkowy (co najmniej ${MIN_OTHER_NOTE} znaków)` : 'Opcjonalnie: z kim, kiedy'}
        slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'pre-order-agreement-source-note' } }}
      />
    </Box>
  );
};

export interface FieldSource {
  kind: string;
  label: string;
  note: string | null;
  by: string | null;
  at: string;
}

/**
 * Źródło bieżącej wartości pól przy wierszu karty prezlecenia: „źródło:
 * telefon”, opis i autor w dymku. Kilka pól w wierszu (np. miasto i data
 * załadunku) — każde inne źródło osobno.
 */
export const FieldSourceTags = ({
  sources,
  fields,
}: {
  sources: Record<string, FieldSource> | undefined;
  fields: readonly string[];
}) => {
  const seen = new Set<string>();
  const tags = fields
    .map((f) => sources?.[f])
    .filter((s): s is FieldSource => !!s)
    .filter((s) => {
      const key = `${s.kind}|${s.note ?? ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  if (tags.length === 0) return null;
  return (
    <>
      {tags.map((s) => (
        <Tooltip key={`${s.kind}|${s.note ?? ''}`} title={[s.note, s.by].filter(Boolean).join(' · ') || s.label}>
          <Chip
            size="small"
            variant="outlined"
            sx={{ ml: 0.5 }}
            data-testid="pre-order-field-source"
            label={`źródło: ${s.label}${s.note ? ` — ${s.note}` : ''}`}
          />
        </Tooltip>
      ))}
    </>
  );
};
